/**
 * OpVisualGrid - OPVisual's clip grid on the iPad.
 *
 * Laid out the way OPVisual's own grid is, so there is nothing to translate
 * between the two screens: columns across the top (tap one = fire the whole
 * column, one look), layers stacked with **L5 at the top and L1 at the
 * bottom** (OPVisual draws "top layer first so the picture reads the same way
 * the composite stacks"), and each row's gutter carrying that layer's on/off,
 * blend, speed, clear and opacity - the same controls OPVisual's gutter has.
 *
 * Above: launch moment (BAR / NOW), OPVisual's health, SHAKE, STOP ALL, B/OUT,
 * SCREEN. Below: the visualiser knobs, then master opacity and the look.
 * Cells are 16:9, the shape of the picture OPVisual sends (DEC-272).
 *
 * ## What it shows, and where each thing comes from
 *
 * - LOADED: OPVisual's own picture and name per cell (`opvisual/listing.ts`).
 * - ON AIR / NEXT BAR: what OPVisual REPORTS (`opvisual/live.ts`, DEC-272):
 *   its `state` frame says, per lane, which clip is on air and whether a
 *   bar-snapped fire is waiting. NEXT BAR is drawn on the cell this grid last
 *   sent to that lane, since OPVisual does not say which cell is waiting.
 * - SENT: the fallback while OPVisual's report is not reaching us (OPVisual
 *   closed, or an OPBridge without the relay): the cell this grid last fired,
 *   marked as sent and never as on air.
 * - Sliders and gutter keys show OPVisual's reported values when it is
 *   reporting, and where this grid last put them otherwise. The visualiser
 *   knobs always show where they were last put: OPVisual reports none.
 *
 * ## Safety
 *
 * - An unaimed grid sends nothing (see `sendOpVisual`) and says why over the
 *   whole surface.
 * - On the OPBridge channel (DEC-215, the default), a press that no OPVisual
 *   heard is reported, not marked sent, and the corner reads NOT LINKED with a
 *   fault edge until a press finds OPVisual again. The grid stays playable in
 *   that state: the next press is the re-check.
 * - No PANIC and no chain on this surface (operator, 2026-10-12): B/OUT is the
 *   way to black, and STOP ALL clears every layer. B/OUT is OPVisual's own
 *   blackout latch: a second press brings the picture back, and so does
 *   raising MASTER (see `blackoutMsg`). PANIC stays available to a Trigger.
 * - SHAKE is held: on at the press, off at the release, and off again if the
 *   grid goes away mid-hold, so a lost release cannot leave the room shaking.
 */

import { createEffect, createSignal, For, Show, onCleanup, untrack } from 'solid-js';
import { editItem, editOwnsGesture, selectedItemId, type LayoutColor, type OpVisualGridItem } from '../layout/store.ts';
import { HUE_VALUES, type PaletteHue } from '../layout/projectStore.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import {
  fireOpVisualAction, opvFault, opvNotLinked, reportOpv, sendLayerOpacity, sendLayerEnabled,
  sendLayerBlend, sendLayerSpeed, sendMaster, sendLook, sendCamShake, sendVizKnob, sendVizNewTake,
  makeSendThrottle, type OpvAim,
} from '../opvisual/fire.ts';
import { opvSession, setLayerLocal, setMasterLocal, setLookLocal } from '../opvisual/session.ts';
import { cellKey, opvListing, watchOpvListing } from '../opvisual/listing.ts';
import { opvPreview, watchOpvPreview, type OpvPreviewState } from '../opvisual/preview.ts';
import { vizKnobName } from '../timeline/opvControls.ts';
import { opvLive, opvLiveUsable, onAirCol, laneArmed, laneLive, recentDropped } from '../opvisual/live.ts';
import {
  OPVISUAL_COLS, OPVISUAL_LAYERS, OPV_BLENDS, OPV_BLEND_SHORT, OPV_SPEEDS, OPV_LOOK_PARAMS,
  cellLabel, type OpVisualAction, type OpvBlend, type OpvLookParam,
} from '../opvisual/wire.ts';
import type { FireResult } from '../opbridge/dispatch.ts';

interface Props {
  item: OpVisualGridItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

const LOOK_LABEL: Record<OpvLookParam, string> = {
  brightness: 'BRIGHT', hue: 'HUE', sat: 'SAT', bloom: 'BLOOM',
};

/** Below this, OPVisual is visibly struggling. */
const FPS_WARN = 50;

export function OpVisualGrid(props: Props) {
  const fault = () => opvFault(props.item.bridgeIds);
  const live = () => fault() === null;
  const aim = (): OpvAim => ({ bridgeIds: props.item.bridgeIds, via: props.item.sendVia });
  const notLinked = () => opvNotLinked(aim());
  // What is loaded in OPVisual's grid, read while this surface is mounted.
  createEffect(() => {
    const release = watchOpvListing(props.item.bridgeIds?.[0]);
    onCleanup(release);
  });
  const listing = () => opvListing(props.item.bridgeIds?.[0]);
  /** DEC-320: the program monitor is Studio's centrepiece, so a grid saved without the setting shows it; only an
   *  explicit Off hides it. */
  const monitorOn = () => props.item.showMonitor !== false;
  // The program monitor's picture, polled only while it is shown here.
  createEffect(() => {
    if (!monitorOn()) return;
    const target = props.item.bridgeIds?.[0];
    // Untracked: the watch reads the bridges signal, and a re-run here would
    // drop and re-fetch the picture (a blank monitor) on every bridge update.
    const release = untrack(() => watchOpvPreview(target));
    onCleanup(release);
  });
  const preview = () => (monitorOn() ? opvPreview(props.item.bridgeIds?.[0]) : null);
  /** OPVisual's own width once read (its CONTRACT asks controllers to read
   *  it, not hard-code it); the constant until then. */
  const maxCols = () => listing()?.cols || OPVISUAL_COLS;
  const cols = () => Math.max(1, Math.min(maxCols(), Math.round(props.item.cols || maxCols())));
  const colList = () => Array.from({ length: cols() }, (_, i) => i + 1);
  /** L5 first: top of the surface is the top of the composite. */
  const layerList = Array.from({ length: OPVISUAL_LAYERS }, (_, i) => OPVISUAL_LAYERS - i);
  /** Follow off (2026-10-18): the grid's cells and columns fire with
   *  `follow: false`, so a cell counts its plays and ends without following. */
  const noFollow = (): { follow?: false } => (props.item.follow === false ? { follow: false } : {});
  /** A layer's own colour, or undefined when it follows the item's. */
  const layerHue = (layer: number): PaletteHue | undefined => {
    const h = props.item.layerHues?.[layer - 1];
    return h && h !== 'none' ? h : undefined;
  };

  // OPVisual's own report. A 1 s tick re-checks whether it is still fresh, so
  // a closed OPVisual drops back to SENT marks without waiting for a frame.
  const [now, setNow] = createSignal(Date.now());
  const tick = window.setInterval(() => setNow(Date.now()), 1000);
  const rep = () => opvLive(props.item.bridgeIds?.[0]);
  const reporting = () => opvLiveUsable(rep(), now());
  const assetAt = (col: number, row: number): string | null => listing()?.cells[cellKey(col, row)]?.assetId ?? null;
  const airCol = (layer: number): number | null => {
    const r = rep();
    return r && reporting() ? onAirCol(r, layer, assetAt, cols()) : null;
  };

  // Which cell/header was just pressed, for the tap flash. Keyed by a string
  // so a cell and a column header can never collide.
  const [flash, setFlash] = createSignal<string | null>(null);
  let flashTimer: number | undefined;
  function flashKey(k: string): void {
    setFlash(k);
    if (flashTimer !== undefined) clearTimeout(flashTimer);
    flashTimer = window.setTimeout(() => setFlash(null), 140);
  }

  // SHAKE: held. Its amount is this grid's own setting.
  const [shaking, setShaking] = createSignal(false);
  const [shakeAmount, setShakeAmount] = createSignal(0.5);
  function shakeDown(e: PointerEvent): void {
    if (!mayAct(e)) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setShaking(true);
    void sendCamShake(true, shakeAmount(), aim()).then((r) => report(r));
  }
  function shakeUp(): void {
    if (!shaking()) return;
    setShaking(false);
    void sendCamShake(false, 0, aim()).then((r) => report(r));
  }

  // The visualiser strip: which layer the knobs act on (0 = the newest on air),
  // and where each knob was last put. OPVisual reports none of these back.
  const [vizLayer, setVizLayer] = createSignal(0);
  const [vizKnobs, setVizKnobs] = createSignal<Array<number | null>>([null, null, null, null, null, null]);
  /** A knob's own name on the visualiser it acts on, when OPVisual is reporting one on air (2026-11-02: "the knobs needs
   *  names not numbers"); which layer OPVisual picks for "AIR" is its newest, so the highest lane on air stands in. */
  const knobLabel = (k: number): string => {
    const r = rep();
    const lanes = r && reporting() ? r.lanes.filter((l) => l.onAir && l.assetId?.startsWith('viz.')) : [];
    const on = vizLayer() === 0 ? lanes[lanes.length - 1] : lanes.find((l) => l.lane === vizLayer());
    return vizKnobName(on?.assetId, k) ?? `K${k}`;
  };

  onCleanup(() => {
    if (flashTimer !== undefined) clearTimeout(flashTimer);
    clearInterval(tick);
    if (shaking()) void sendCamShake(false, 0, aim());
  });

  /** Common gate for every press on this surface. */
  function mayAct(e: PointerEvent): boolean {
    if (editOwnsGesture(props.item.id)) return false;
    if (selectedItemId() === props.item.id) return false;
    if (!live()) return false;
    e.stopPropagation();
    return true;
  }

  const report = (r: FireResult, okText?: string): void => reportOpv(r, okText);

  // Guard (DEC-320, operator: "yes"): with it on, STOP ALL, B/OUT and a layer's CLR need a second tap (the first arms
  // them, a ring drains); cells and columns never ask, they are the instrument.
  const guard = createDiscreteGuard(() => !!props.item.guard);
  function guarded(e: PointerEvent, key: string, run: () => void): void {
    if (!mayAct(e)) return;
    if (!guard.gate(key)) return;
    run();
  }

  function act(e: PointerEvent, action: OpVisualAction, key: string, okText?: string): void {
    if (!mayAct(e)) return;
    flashKey(key);
    void fireOpVisualAction(action, props.item.quant, aim()).then((r) => report(r, okText));
  }

  function setQuant(e: PointerEvent, q: 'bar' | 'now'): void {
    // Not gated on `live`: choosing the launch moment is setup, and it is
    // fine to do before the machine is reachable.
    if (editOwnsGesture(props.item.id)) return;
    if (selectedItemId() === props.item.id) return;
    e.stopPropagation();
    if (props.item.quant !== q) editItem(props.item.id, { quant: q });
  }

  /** Pick the visualiser strip's layer: setup, so not gated on `live`. */
  function pickVizLayer(e: PointerEvent, layer: number): void {
    if (editOwnsGesture(props.item.id)) return;
    if (selectedItemId() === props.item.id) return;
    e.stopPropagation();
    setVizLayer(layer);
  }

  const s = opvSession;

  /** OPVisual's reported value when it is reporting, else null. */
  const rLane = (layer: number) => {
    const r = rep();
    return r && reporting() ? laneLive(r, layer) : null;
  };
  const shownEnabled = (layer: number): boolean => rLane(layer)?.enabled ?? s().layers[layer - 1]!.enabled;
  const shownBlend = (layer: number): OpvBlend => {
    const b = rLane(layer)?.blend;
    return b && (OPV_BLENDS as readonly string[]).includes(b) ? (b as OpvBlend) : s().layers[layer - 1]!.blend;
  };
  const shownSpeed = (layer: number): number => rLane(layer)?.speed ?? s().layers[layer - 1]!.speed;
  const blackedOut = () => (reporting() ? rep()?.blackout === true : false);

  const health = () => {
    const r = rep();
    if (!r || now() - r.heartbeatAt > 4000 || r.fps === null) return null;
    const dropped = recentDropped(r, now());
    return { fps: Math.round(r.fps), dropped, warn: dropped > 0 || r.fps < FPS_WARN };
  };

  return (
    <div
      class="opv-grid"
      data-color={COLOR_TOKEN[props.item.color]}
      data-live={live() ? 'true' : 'false'}
      data-link={notLinked() ? 'down' : 'ok'}
      data-reporting={reporting() ? 'true' : 'false'}
      data-layer-controls={props.item.showLayerControls ? 'true' : 'false'}
      style={{ '--opv-cols': String(cols()) }}
    >
      {/* ── Top bar ── */}
      <div class="opv-topbar">
        <span class="opv-title">{props.item.hideName ? '' : (props.item.name ?? 'OPVISUAL')}</span>
        <div class="opv-seg" role="group" aria-label="Launch moment">
          <button
            type="button" class="opv-btn" data-selected={props.item.quant === 'bar' ? 'true' : 'false'}
            onPointerDown={(e) => setQuant(e, 'bar')}
            title="Cells and columns land on the next bar line"
          >BAR</button>
          <button
            type="button" class="opv-btn" data-selected={props.item.quant === 'now' ? 'true' : 'false'}
            onPointerDown={(e) => setQuant(e, 'now')}
            title="Cells and columns land immediately"
          >NOW</button>
        </div>
        <span class="opv-spacer" />
        <span
          class="opv-health"
          data-state={health() ? (health()!.warn ? 'warn' : 'ok') : 'none'}
          title="OPVisual's own heartbeat, once a second: frames per second, and frames dropped in the last 10 seconds"
        >
          <i />
          <Show when={health()} fallback={<span>no heartbeat</span>}>
            {(h) => <span><b>{h().fps}</b> fps · {h().dropped} dropped</span>}
          </Show>
        </span>
        <button
          type="button" class="opv-btn opv-shake" data-on={shaking() || (reporting() && rep()?.camShake?.on) ? 'true' : 'false'}
          onPointerDown={shakeDown}
          onPointerUp={shakeUp}
          onPointerCancel={shakeUp}
          onLostPointerCapture={shakeUp}
          title="Hold to shake every camera or Spout layer in OPVisual. Amount on the slider beside it."
        >SHAKE</button>
        <div class="opv-shake-amount">
          <OpvSlider
            label=""
            value={shakeAmount()}
            touched={true}
            enabled={live()}
            itemId={props.item.id}
            defaultValue={0.5}
            onLocal={(v) => setShakeAmount(v)}
            send={(v) => (shaking() ? sendCamShake(true, v, aim()) : Promise.resolve({ kind: 'ok' } as FireResult))}
            title="Shake amount"
          />
        </div>
        <button
          type="button" class="opv-btn" data-tone="warn" data-flash={flash() === 'stop' ? 'true' : 'false'}
          data-armed={guard.armedKey() === 'stop' ? 'true' : 'false'}
          onPointerDown={(e) => guarded(e, 'stop', () => { flashKey('stop'); void fireOpVisualAction({ verb: 'stopAll' }, props.item.quant, aim()).then((r) => report(r, 'OPVisual: every layer off air')); })}
          title="OPVisual's own STOP ALL: every layer off air, and anything queued for the bar dropped"
        >STOP ALL</button>
        <button
          type="button" class="opv-btn" data-tone="warn" data-on={blackedOut() ? 'true' : 'false'}
          data-flash={flash() === 'blackout' ? 'true' : 'false'}
          data-armed={guard.armedKey() === 'blackout' ? 'true' : 'false'}
          onPointerDown={(e) => guarded(e, 'blackout', () => { flashKey('blackout'); void fireOpVisualAction({ verb: 'blackout' }, props.item.quant, aim()).then((r) => report(r, reporting() ? undefined : 'OPVisual blackout toggled - press again to bring the picture back')); })}
          title="OPVisual's blackout, on or off - the same as its own B/OUT. Press again, or raise MASTER, to bring the picture back."
        >B/OUT</button>
        <button
          type="button" class="opv-btn" data-flash={flash() === 'screen' ? 'true' : 'false'}
          onPointerDown={(e) => act(e, { verb: 'operatorScreen' }, 'screen', 'OPVisual screen hide / show sent')}
          title="Hide or bring back OPVisual's own screen, in or out of GIG."
        >SCREEN</button>
      </div>

      {/* ── Upper half (DEC-320 Studio): the program monitor, the centrepiece, beside the layer mixer ── */}
      <Show when={monitorOn() || props.item.showLayerControls}>
        <div class="opv-up" data-monitor={monitorOn() ? 'true' : 'false'}>
          <Show when={monitorOn()}>
            <div class="opv-monitor" data-state={preview()?.state ?? 'loading'} title="OPVisual's program output">
              <span class="opv-monitor-bezel">
                <b>PROGRAM</b><span>OPVisual output</span>
                <span class="opv-monitor-live" data-st={!reporting() ? 'none' : blackedOut() ? 'bout' : 'live'}>
                  {!reporting() ? 'NO SIGNAL' : blackedOut() ? 'BLACKOUT' : 'LIVE'}
                </span>
              </span>
              <span class="opv-monitor-scr">
                <Show when={preview()?.url}>
                  {(src) => <img class="opv-monitor-img" src={src()} alt="" draggable={false} />}
                </Show>
                <Show when={(preview()?.state ?? 'loading') !== 'live'}>
                  <span class="opv-monitor-msg">{MONITOR_MESSAGE[preview()?.state ?? 'loading']}</span>
                </Show>
              </span>
            </div>
          </Show>
          <Show when={props.item.showLayerControls}>
            <div class="opv-mix">
              <div class="opv-mix-head"><span>LAYER</span><span>ON AIR</span><span>OPACITY</span><span>ON · BLEND · SPEED · CLEAR</span></div>
              <For each={layerList}>
                {(layer) => {
                  const ls = () => s().layers[layer - 1]!;
                  const lr = () => rLane(layer);
                  /** The column on air (reported), else the one last sent (not reporting). */
                  const col = (): number | null => (reporting() ? airCol(layer) : s().sentCell[layer - 1] ?? null);
                  const info = () => { const c = col(); return c ? listing()?.cells[cellKey(c, layer)] ?? null : null; };
                  const nextCol = (): number | null => {
                    const r = rep();
                    return r && reporting() && laneArmed(r, layer) ? s().sentCell[layer - 1] ?? null : null;
                  };
                  return (
                    <div class="opv-mix-row" data-layer={layer}
                      data-air={reporting() && col() ? 'true' : undefined}
                      data-sent={!reporting() && col() ? 'true' : undefined}
                      data-off={shownEnabled(layer) ? undefined : 'true'}
                      style={layerHue(layer) ? { '--hue': HUE_VALUES[layerHue(layer)!] } : undefined}>
                      <span class="opv-mix-ln">L{layer}</span>
                      <span class="opv-mix-th">
                        <Show when={info()?.img}>{(src) => <img src={src()} alt="" draggable={false} />}</Show>
                      </span>
                      <span class="opv-mix-nm">
                        <b>{col() ? (info()?.label ?? cellLabel(col()!, layer)) : '-'}</b>
                        <i>{col()
                          ? `${reporting() ? 'ON AIR' : 'SENT'} · COL ${col()} · ${OPV_BLEND_SHORT[shownBlend(layer)]} · ${shownSpeed(layer)}X`
                          : nextCol() ? `NEXT BAR · COL ${nextCol()}` : 'nothing on air'}</i>
                      </span>
                      <OpvSlider
                        label=""
                        value={ls().opacity}
                        readback={lr()?.opacity ?? null}
                        touched={ls().touched.opacity}
                        enabled={live()}
                        itemId={props.item.id}
                        defaultValue={1}
                        onLocal={(v) => setLayerLocal(layer, 'opacity', v)}
                        send={(v) => sendLayerOpacity(layer, v, aim())}
                        title={`Layer ${layer} opacity`}
                      />
                      <span class="opv-mix-btns">
                        <button
                          type="button" class="opv-mini" data-touched={lr() || ls().touched.enabled ? 'true' : 'false'}
                          data-off={shownEnabled(layer) ? 'false' : 'true'}
                          onPointerDown={(e) => { if (mayAct(e)) void sendLayerEnabled(layer, !shownEnabled(layer), aim()).then((r) => report(r)); }}
                          title={`Layer ${layer} on / off`}
                        >{shownEnabled(layer) ? 'ON' : 'OFF'}</button>
                        <button
                          type="button" class="opv-mini" data-touched={lr() || ls().touched.blend ? 'true' : 'false'}
                          onPointerDown={(e) => {
                            if (!mayAct(e)) return;
                            const i = OPV_BLENDS.indexOf(shownBlend(layer));
                            const next = OPV_BLENDS[(i + 1) % OPV_BLENDS.length]!;
                            void sendLayerBlend(layer, next, aim()).then((r) => report(r));
                          }}
                          title={`Layer ${layer} blend mode - tap to cycle`}
                        >{OPV_BLEND_SHORT[shownBlend(layer)]}</button>
                        <button
                          type="button" class="opv-mini" data-touched={lr() || ls().touched.speed ? 'true' : 'false'}
                          onPointerDown={(e) => {
                            if (!mayAct(e)) return;
                            const i = OPV_SPEEDS.indexOf(shownSpeed(layer));
                            const next = OPV_SPEEDS[(i + 1) % OPV_SPEEDS.length] ?? 1;
                            void sendLayerSpeed(layer, next, aim()).then((r) => report(r));
                          }}
                          title={`Layer ${layer} playback speed - tap to cycle`}
                        >{`${shownSpeed(layer)}x`}</button>
                        <button
                          type="button" class="opv-mini" data-tone="warn"
                          data-armed={guard.armedKey() === `x${layer}` ? 'true' : 'false'}
                          data-flash={flash() === `x${layer}` ? 'true' : 'false'}
                          onPointerDown={(e) => guarded(e, `x${layer}`, () => { flashKey(`x${layer}`); void fireOpVisualAction({ verb: 'clearLayer', layer }, props.item.quant, aim()).then((r) => report(r)); })}
                          title={`Take layer ${layer} off air`}
                        >CLR</button>
                      </span>
                    </div>
                  );
                }}
              </For>
            </div>
          </Show>
        </div>
      </Show>

      {/* ── Grid ── */}
      <div class="opv-body">
        {/* NOT LINKED outranks hideLabel: it is a fault, not a label. It
            matches OPVisual's own bridge pill, which reads LINKED when well. */}
        <div class="opv-corner" data-link={notLinked() ? 'down' : 'ok'}>
          {notLinked() ? 'NOT LINKED' : props.item.hideLabel ? '' : (props.item.quant === 'now' ? 'NOW' : 'ON BAR')}
        </div>
        <For each={colList()}>
          {(col) => (
            <button
              type="button"
              class="opv-colhead"
              data-sent={!reporting() && s().sentColumn === col ? 'true' : 'false'}
              data-flash={flash() === `c${col}` ? 'true' : 'false'}
              data-empty={listing() && !layerList.some((l) => listing()!.cells[cellKey(col, l)]) ? 'true' : 'false'}
              onPointerDown={(e) => act(e, { verb: 'column', col, ...noFollow() }, `c${col}`)}
              title={`Fire column ${col} - one clip per layer, empty layers go dark`}
            >{col}</button>
          )}
        </For>

        <For each={layerList}>
          {(layer) => {
            return (
              <>
                <div class="opv-gutter" data-enabled={shownEnabled(layer) ? 'true' : 'false'}
                  data-air={reporting() && airCol(layer) ? 'true' : undefined}>
                  <span class="opv-layer">L{layer}</span>
                </div>
                <For each={colList()}>
                  {(col) => {
                    const info = () => listing()?.cells[cellKey(col, layer)] ?? null;
                    /** 'on' / 'next' from OPVisual's report; 'sent' only while it is not reporting. */
                    const air = (): 'on' | 'next' | 'sent' | '' => {
                      if (reporting()) {
                        if (airCol(layer) === col) return 'on';
                        const r = rep();
                        if (r && laneArmed(r, layer) && s().sentCell[layer - 1] === col) return 'next';
                        return '';
                      }
                      return s().sentCell[layer - 1] === col ? 'sent' : '';
                    };
                    return (
                    <button
                      type="button"
                      class="opv-cell"
                      data-layer-hue={layerHue(layer)}
                      style={layerHue(layer) ? { '--hue': HUE_VALUES[layerHue(layer)!] } : undefined}
                      data-filled={info() ? 'true' : 'false'}
                      data-img={info()?.img ? 'true' : 'false'}
                      data-name={info()?.label ?? ''}
                      data-air={air()}
                      data-sent={air() === 'sent' ? 'true' : 'false'}
                      data-flash={flash() === `${layer}-${col}` ? 'true' : 'false'}
                      data-layer-off={shownEnabled(layer) ? 'false' : 'true'}
                      onPointerDown={(e) => act(e, { verb: 'cell', col, layer, ...noFollow() }, `${layer}-${col}`)}
                      title={`Fire ${cellLabel(col, layer)}`}
                    >
                      <Show when={info()?.img}>
                        {(src) => <img class="opv-cell-img" src={src()} alt="" draggable={false} />}
                      </Show>
                      <Show when={info()?.label}>
                        {(name) => <span class="opv-cell-name">{name()}</span>}
                      </Show>
                      <Show when={!info()?.label}>
                        <span class="opv-cell-label">{cellLabel(col, layer)}</span>
                      </Show>
                      <Show when={air()}>
                        <span class="opv-cell-sent">{air() === 'on' ? 'ON AIR' : air() === 'next' ? 'NEXT BAR' : 'SENT'}</span>
                      </Show>
                    </button>
                    );
                  }}
                </For>
              </>
            );
          }}
        </For>
      </div>

      {/* ── Bottom zone: the program monitor beside the knobs and look row ── */}
      <div class="opv-bottom" data-monitor="false">
      {/* ── Visualiser knobs ── */}
      <Show when={props.item.showViz !== false}>
        <div class="opv-viz">
          <div class="opv-viz-target" role="group" aria-label="Which layer the knobs act on">
            <span class="opv-viz-label">VIZ</span>
            <For each={[0, ...layerList]}>
              {(l) => (
                <button
                  type="button" class="opv-mini" data-selected={vizLayer() === l ? 'true' : 'false'}
                  onPointerDown={(e) => pickVizLayer(e, l)}
                  title={l === 0 ? 'The newest visualiser on air' : `The visualiser on layer ${l}`}
                >{l === 0 ? 'AIR' : `L${l}`}</button>
              )}
            </For>
          </div>
          <For each={[1, 2, 3, 4, 5, 6]}>
            {(k) => (
              <OpvSlider
                label={knobLabel(k)}
                value={vizKnobs()[k - 1] ?? 0.5}
                touched={vizKnobs()[k - 1] !== null}
                enabled={live()}
                itemId={props.item.id}
                onLocal={(v) => setVizKnobs((cur) => cur.map((x, i) => (i === k - 1 ? v : x)))}
                send={(v) => sendVizKnob(k, v, vizLayer(), aim())}
                title={`Visualiser knob ${k}. Live only: OPVisual does not report it back, and firing the visualiser again brings its stored knobs back.`}
              />
            )}
          </For>
          <button
            type="button" class="opv-btn" data-flash={flash() === 'take' ? 'true' : 'false'}
            onPointerDown={(e) => {
              if (!mayAct(e)) return;
              flashKey('take');
              void sendVizNewTake(vizLayer(), props.item.quant, aim()).then((r) => report(r));
            }}
            title="A new take of the visualiser: a new seed, crossfaded over a bar"
          >NEW TAKE</button>
        </div>
      </Show>

      {/* ── Master + look ── */}
      <Show when={props.item.showMasterLook}>
        <div class="opv-footer">
          <OpvSlider
            label="MASTER"
            value={s().master}
            readback={reporting() ? rep()?.master ?? null : null}
            touched={s().masterTouched}
            enabled={live()}
            itemId={props.item.id}
            defaultValue={1}
            onLocal={(v) => setMasterLocal(v)}
            send={(v) => sendMaster(v, aim())}
            title="Master opacity, applied after the composite"
          />
          <For each={[...OPV_LOOK_PARAMS]}>
            {(p) => (
              <OpvSlider
                label={LOOK_LABEL[p]}
                value={s().look[p] ?? 0.5}
                readback={reporting() ? rep()?.look[p] ?? null : null}
                touched={s().look[p] !== null}
                enabled={live()}
                itemId={props.item.id}
                onLocal={(v) => setLookLocal(p, v)}
                send={(v) => sendLook(p, v, aim())}
                title={`Look: ${p}`}
              />
            )}
          </For>
        </div>
      </Show>
      </div>

      <Show when={!live()}>
        <div class="opv-waiting">
          <span>
            {fault() === 'no-machine'
              ? 'Pick the computer running OPVisual under "Sends to" in the Inspector'
              : 'The OPVisual computer is offline'}
          </span>
        </div>
      </Show>
    </div>
  );
}

/** What the program monitor says when it has no live picture. */
const MONITOR_MESSAGE: Record<OpvPreviewState, string> = {
  loading: 'Connecting...',
  live: '',
  'no-route': "This computer's OPBridge needs an OPBridge update to show the picture",
  'no-opvisual': 'OPVisual is not running',
  'no-scope': 'This pairing cannot see OPVisual: pair again',
  offline: 'The OPVisual computer is offline',
};

/** How long after a release the slider keeps showing its own value, so a
 *  readback still carrying the old value cannot snap it back mid-gesture. */
const HOLD_LOCAL_MS = 900;

/**
 * A horizontal absolute slider: tap anywhere to jump, drag to move, double-tap
 * to reset (when there is a known default). Moves the local position every
 * frame and sends through a throttle, forcing the final value on release -
 * the Mixer's proven pattern. Shows its number only once touched or read
 * back: an untouched, unreported slider's position is a default, not a reading.
 *
 * `readback` (DEC-272) is OPVisual's reported value. It is shown whenever the
 * slider is not being moved, so the slider follows changes made elsewhere
 * (OPVisual's own screen, a Trigger, a timeline).
 */
function OpvSlider(props: {
  label: string;
  value: number;
  readback?: number | null;
  touched: boolean;
  enabled: boolean;
  itemId: string;
  defaultValue?: number;
  onLocal: (v: number) => void;
  send: (v: number) => Promise<FireResult>;
  title: string;
}) {
  const throttle = makeSendThrottle((v) => props.send(v));
  onCleanup(() => throttle.cleanup());
  let el: HTMLDivElement | undefined;
  let dragging = false;
  let lastTap = 0;
  // True from a press until HOLD_LOCAL_MS after the release: the slider shows
  // its own value, never the readback, while it is (just) being moved.
  const [holding, setHolding] = createSignal(false);
  let holdTimer: number | undefined;
  onCleanup(() => { if (holdTimer !== undefined) clearTimeout(holdTimer); });

  function holdLocal(): void {
    setHolding(true);
    if (holdTimer !== undefined) clearTimeout(holdTimer);
    holdTimer = window.setTimeout(() => { holdTimer = undefined; if (!dragging) setHolding(false); }, HOLD_LOCAL_MS);
  }
  const shown = (): number => {
    const rb = props.readback;
    if (rb === null || rb === undefined) return props.value;
    if (dragging || holding()) return props.value;
    return rb;
  };
  const isRead = () => props.readback !== null && props.readback !== undefined;

  function valueAt(clientX: number): number {
    if (!el) return props.value;
    const r = el.getBoundingClientRect();
    if (r.width <= 0) return props.value;
    return Math.max(0, Math.min(1, (clientX - r.left) / r.width));
  }

  function onDown(e: PointerEvent): void {
    if (editOwnsGesture(props.itemId)) return;
    if (selectedItemId() === props.itemId) return;
    if (!props.enabled) return;
    e.stopPropagation();
    const t = performance.now();
    if (props.defaultValue !== undefined && t - lastTap < 300) {
      lastTap = 0;
      props.onLocal(props.defaultValue);
      throttle.force(props.defaultValue);
      holdLocal();
      return;
    }
    lastTap = t;
    dragging = true;
    holdLocal();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const v = valueAt(e.clientX);
    props.onLocal(v);
    throttle.schedule(v);
  }
  function onMove(e: PointerEvent): void {
    if (!dragging) return;
    const v = valueAt(e.clientX);
    props.onLocal(v);
    throttle.schedule(v);
  }
  function onUp(e: PointerEvent): void {
    if (!dragging) return;
    dragging = false;
    holdLocal();
    throttle.force(valueAt(e.clientX));
  }

  return (
    <div class="opv-slider" data-touched={props.touched || isRead() ? 'true' : 'false'} data-read={isRead() ? 'true' : 'false'} title={props.title}>
      <span class="opv-slider-label">{props.label}</span>
      <div
        class="opv-slider-track"
        ref={el}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        <div class="opv-slider-fill" style={{ width: `${Math.round(shown() * 100)}%` }} />
      </div>
      <span class="opv-slider-value">{props.touched || isRead() ? `${Math.round(shown() * 100)}` : '--'}</span>
    </div>
  );
}
